/**
 * -----------------------------------------------------------------------------
 * Driver Edit Form
 * -----------------------------------------------------------------------------
 * These components are used to render the form for editing the driver details.
 */

import { FormikProps } from "formik";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { GroupBase, OptionsOrGroups } from "react-select";
import {
  useLazyOrganizationVehiclesQuery,
  useOrganizationVehiclesQuery,
} from "../../../api/network/adminApiServices";
import { setModalsData, TModalsState } from "../../../api/store/commonSlice";
import { LegacyRuleEntityListingPayload } from "../../../api/types/Rule";
import { TLegacyVehicle } from "../../../api/types/Vehicle";
import svgIcons from "../../../assets/svg/svgIcons";
import { AdminFormFileInput } from "../../../components/admin/fileFields";
import {
  AdminFormFieldAsyncLazyDropdown,
  AdminFormFieldCheckbox,
  AdminFormFieldDropdown,
  AdminFormFieldInput,
  AdminFormFieldSubmit,
  TSelectboxOption,
} from "../../../components/admin/formFields";
import ModalDeleteImageConfirmation from "../../../components/admin/modalDeleteImageConfirmation";
import TooptipInfoButton from "../../../components/tooltipInfoButton";
import { APP_CONFIG } from "../../../constants/constants";
import { ACL_NAMES, useACL } from "../../../hooks/acl";
import { asyncDebounce } from "../../../utils/common";
import { formattedDateTime } from "../../../utils/dateTime";
import { useLoggedInUserData } from "../../../utils/user";
import { consistentDisplayAssetItem } from "../../../utils/vehicle";
import { ROLE_ACCESS_LEVELS } from "../role/constants";
import {
  LICENSE_STATE_OPTIONS,
  LICENSE_STATUS_OPTIONS,
  LICENSE_TYPE_OPTIONS,
} from "./constants";
import { driverUploadFormInitialValues } from "./validation";
interface DriverGeneralDetailFormProps {
  values: any;
  errors: any;
  touched: any;
  handleChange: (event: React.ChangeEvent<any>) => void;
  formikSetValue: (field: string, value: any, shouldValidate?: boolean) => void;
  handleBlur: (event: React.FocusEvent<any>) => void;
  formikSetTouched: (
    field: string,
    isTouched?: boolean,
    shouldValidate?: boolean
  ) => void;
  userCanEdit: boolean;
  uploadFormik?: FormikProps<typeof driverUploadFormInitialValues>;
  loadingData: boolean;
}

export const DriverGeneralDetailForm: FC<DriverGeneralDetailFormProps> = ({
  values,
  errors,
  touched,
  handleChange,
  formikSetValue,
  handleBlur,
  formikSetTouched,
  userCanEdit,
  uploadFormik,
  loadingData,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.drivers.detailsPage.form",
  });
  const dispatch = useDispatch();
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );

  const {
    values: uploadFormValues,
    errors: uploadFormErrors,
    handleSubmit: uploadFormHandleSubmit,
    setFieldValue: uploadFormSetFieldValue,
  } = uploadFormik || {};
  const [fileName, setFileName] = useState<string | null>(null);

  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");
  const thisOrgDriverGroupAllowed = useLoggedInUserData("hasdrivers");
  const orgVehiclesQueryParams = {
    accountid: `${thisUserOrganizationId}`,
    page: 1,
    page_size: 100,
  };

  const { data: dataOrgVehicles } = useOrganizationVehiclesQuery(
    orgVehiclesQueryParams
    // 'search_fields' intentionally not added here as default search fields are defined in the API service
  );
  const { results: allVehicles } = dataOrgVehicles ?? {};
  const allVehicleList = !!allVehicles
    ? (allVehicles || ([] as TLegacyVehicle[])).map(
        (item: TLegacyVehicle, index: number) => ({
          value: item?.deviceid,
          label: item?.vehiclemodel + " " + item?.vehiclemake,
        })
      )
    : [];
  const { getUserACLAccessLevel } = useACL();

  const {
    data: dataOrgInitialVehicles,
    isFetching: isFetchingInitialOrgVehicles,
  } = useOrganizationVehiclesQuery({
    accountid: `${thisUserOrganizationId}`,
    page: 1,
    page_size: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
    search: "",
    // 'search_fields' intentionally not added here as default search fields are defined in the API service
  } as LegacyRuleEntityListingPayload);

  const [orgVehiclesQuery, {}] = useLazyOrganizationVehiclesQuery();
  const [dropdownVehicleSearchResults, setDropdownVehicleSearchResults] =
    useState<TLegacyVehicle[]>([]);

  const loadOptionsHandlerVehicle = async (
    inputValue: string,
    prevOptions: OptionsOrGroups<unknown, GroupBase<unknown>>,
    additional?: { page: number }
  ) => {
    const { data: groupsVehicleResponse } = await orgVehiclesQuery({
      accountid: `${thisUserOrganizationId}`,
      page: additional?.page || 1,
      page_size: APP_CONFIG.LISTINGS.DROPDOWN_SEARCH_PAGE_SIZE,
      search: inputValue,
      // 'search_fields' intentionally not added here as default search fields are defined in the API service
    });

    if (!groupsVehicleResponse || !groupsVehicleResponse?.results) {
      return {
        options: [],
        hasMore: false,
        additional: {
          page: 1,
        },
      };
    }
    const { results } = groupsVehicleResponse as {
      results: TLegacyVehicle[];
    };
    setDropdownVehicleSearchResults(results);
    return {
      options: !!results
        ? (results || ([] as TLegacyVehicle[])).map(
            (item: TLegacyVehicle, index: number) =>
              ({
                value: item.deviceid,
                label: consistentDisplayAssetItem(
                  item.deviceid,
                  item.description,
                  item.displayname,
                  item.drivername
                ),
                // label: `${item.deviceid}${item.description ? ` | ${item.description}` : ""}${item.displayname ? ` | ${item.displayname}` : ""}`,
              }) as TSelectboxOption
          )
        : [],
      hasMore: groupsVehicleResponse?.next ? true : false,
      additional: {
        page: (additional?.page as number) + 1 || 2,
      },
    };
  };
  const debouncedLoadOptionsHandlerVehicle = asyncDebounce(
    loadOptionsHandlerVehicle,
    APP_CONFIG.DEBOUNCE.SEARCH
  );

  // reset image selection field on image change (API)
  useEffect(() => {
    uploadFormSetFieldValue?.("image", null);
  }, [values?.driver_image_url]);

  const getSelectedVehicleLabel = () => {
    if (!values?.vehicle_id || values.vehicle_id === "-") return undefined;

    const vehicleDetails =
      allVehicles?.find((v) => v.deviceid === values.vehicle_id) ||
      dropdownVehicleSearchResults?.find(
        (v) => v.deviceid === values.vehicle_id
      );

    if (vehicleDetails) {
      return consistentDisplayAssetItem(
        vehicleDetails.deviceid,
        vehicleDetails.description,
        vehicleDetails.displayname,
        vehicleDetails.drivername
      );
    }
    return values.vehicle_id;
  };

  const selectedVehicleLabel = getSelectedVehicleLabel();

  return (
    <div className="px-5 pt-4 pb-8 bg-gray-100 grid grid-cols-12 gap-4">
      <div className="col-span-12 row-span-2 flex flex-col items-center">
        <div
          className="flex items-center justify-center bg-white bg-cover bg-no-repeat bg-center inset-shadow-sm inset-shadow-red-200 size-36 rounded-full ring-2 ring-gray-300"
          style={
            !!values.driver_image_url
              ? { backgroundImage: `url(${values.driver_image_url})` }
              : {}
          }
        >
          {!values.driver_image_url && (
            <span className="text-xs font-bold text-gray-400">
              {t("no_image")}
            </span>
          )}
        </div>
        <form className="flex gap-2 mt-4" onSubmit={uploadFormHandleSubmit}>
          <AdminFormFileInput
            label={
              <span className="flex justify-center items-center gap-2 whitespace-nowrap">
                <img
                  // className="ml-2 transition-all group-focus:grayscale-0 group-focus:brightness-0 group-focus:invert group-hover:grayscale-0 group-hover:brightness-0 group-hover:invert"
                  className={`${loadingData ? "size-6" : "size-4"}${loadingData || !userCanEdit ? " grayscale opacity-50" : ""}`}
                  src={
                    loadingData ? svgIcons.LoadingAnimation : svgIcons.Upload
                  }
                  alt={t("upload")}
                />
                {t("upload")}
              </span>
            }
            id="File"
            onChange={(file) => {
              setFileName(file?.name || null);
              uploadFormSetFieldValue?.("image", file);
              uploadFormik?.submitForm();
            }}
            fileName={fileName}
            value={uploadFormValues?.image}
            error={uploadFormErrors?.image}
            disabled={loadingData || !userCanEdit}
            customWrapperClass="relative"
            allowedFields={APP_CONFIG.DOCUMENT_UPLOAD.ALLOWED_IMAGE_FILE_EXT}
            buttonSkin={true}
          />
          {!!values.driver_image_url && (
            <AdminFormFieldSubmit
              type="button"
              variant="danger"
              buttonDimensionClass="h-10 w-full px-2.5"
              customWrapperClass="relative group"
              label={
                <img
                  // className="transition-all group-focus:grayscale-0 group-focus:brightness-0 group-focus:invert group-hover:grayscale-0 group-hover:brightness-0 group-hover:invert"
                  className={`size-[14px] mx-[2px]${loadingData || !userCanEdit ? " grayscale opacity-50" : ""}`}
                  src={svgIcons.CrossRedIcon}
                  alt={t("upload_info")}
                />
              }
              disabled={loadingData || !userCanEdit}
              onClick={() => {
                dispatch(
                  setModalsData({
                    ...modalsState,
                    showDeleteImageConfirmation: true,
                  })
                );
              }}
            />
          )}
          <TooptipInfoButton text={t("upload_info")} />
        </form>
      </div>
      <ModalDeleteImageConfirmation
        handleDeleteAdmin={() => {
          setFileName(null);
          uploadFormSetFieldValue?.("image", null);
          uploadFormik?.submitForm();
        }}
      />

      <AdminFormFieldInput
        label={t("driver_id")}
        type="text"
        id="driver_id"
        name="driver_id"
        value={values.driver_id}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.driver_id}
        touched={touched.driver_id}
        disabled={!userCanEdit}
        detailsFormField={true}
        readOnly={true}
        customWrapperClass="col-span-6"
      />

      <AdminFormFieldInput
        label={t("driver_name")}
        type="text"
        id="driver_name"
        name="driver_name"
        value={values.driver_name}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.driver_name}
        touched={touched.driver_name}
        disabled={!userCanEdit}
        detailsFormField={true}
        customWrapperClass="col-span-6"
      />

      {/* <AdminFormFieldInput
        label={t("nick_name")}
        type="text"
        id="nick_name"
        name="nick_name"
        value={values.nick_name}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.nick_name}
        touched={touched.nick_name}
        disabled={!userCanEdit}
        detailsFormField={true}
      /> */}

      <AdminFormFieldInput
        label={t("contact_phone")}
        type="text"
        id="contact_phone"
        name="contact_phone"
        value={values.contact_phone}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.contact_phone}
        touched={touched.contact_phone}
        disabled={!userCanEdit}
        detailsFormField={true}
        customWrapperClass="col-span-6"
      />
      <AdminFormFieldInput
        label={t("contact_email")}
        type="text"
        id="contact_email"
        name="contact_email"
        value={values.contact_email}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.contact_email}
        touched={touched.contact_email}
        disabled={!userCanEdit}
        detailsFormField={true}
        customWrapperClass="col-span-6"
      />
      {thisOrgDriverGroupAllowed === "1" &&
        getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_DRIVER_BADGE) >=
          ROLE_ACCESS_LEVELS.READ && (
          <AdminFormFieldInput
            label={t("badge_employee_id")}
            type="text"
            id="badge_employee_id"
            name="badge_employee_id"
            value={values.badge_employee_id}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.badge_employee_id}
            touched={touched.badge_employee_id}
            disabled={
              getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_DRIVER_BADGE) <
                ROLE_ACCESS_LEVELS.WRITE || !userCanEdit
            }
            detailsFormField={true}
          />
        )}
      {thisOrgDriverGroupAllowed === "1" &&
        getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_DRIVER_CARD) >=
          ROLE_ACCESS_LEVELS.READ && (
          <AdminFormFieldInput
            label={t("card_id")}
            type="text"
            id="card_id"
            name="card_id"
            value={values.card_id}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.card_id}
            touched={touched.card_id}
            disabled={
              getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_DRIVER_CARD) <
                ROLE_ACCESS_LEVELS.WRITE || !userCanEdit
            }
            detailsFormField={true}
          />
        )}
      {thisOrgDriverGroupAllowed === "1" &&
        getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_DRIVER_ACTIVE) >=
          ROLE_ACCESS_LEVELS.READ && (
          <div className="col-span-12 grid grid-cols-8">
            <AdminFormFieldCheckbox
              customWrapperClass="col-span-4 lg:mr-2"
              label={t("is_active")}
              id="is_active"
              type="checkbox"
              name="is_active"
              checked={values.is_active}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.is_active}
              touched={touched.is_active}
              disabled={
                getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_DRIVER_ACTIVE) <
                  ROLE_ACCESS_LEVELS.WRITE || !userCanEdit
              }
              detailsFormField={true}
            />
          </div>
        )}
      <AdminFormFieldAsyncLazyDropdown
        key={`assigned_asset_${values?.vehicle_id}`}
        loadingData={loadingData || isFetchingInitialOrgVehicles}
        label={t("vehicle_id")}
        id="vehicle_id"
        name="vehicle_id"
        // defaultOptions={
        //   dataOrgInitialVehicles?.results?.map((vehicle) => ({
        //     value: vehicle?.deviceid,
        //     label: vehicle?.deviceid,
        //   })) || [] // Ensuring a fallback to an empty array if results are undefined
        // }
        defaultOption={
          !!values?.vehicle_id
            ? values.vehicle_id === "-"
              ? undefined
              : { value: values.vehicle_id, label: selectedVehicleLabel }
            : undefined
        }
        loadOptions={debouncedLoadOptionsHandlerVehicle}
        onChange={(e: any) => {
          formikSetValue("vehicle_id", e?.value);
        }}
        onBlur={(e: any) => {
          formikSetTouched("vehicle_id", true);
          handleBlur(e);
        }}
        touched={touched.vehicle_id}
        error={errors.vehicle_id}
        disabled={!userCanEdit}
        detailsFormField={true}
      />
    </div>
  );
};

export const DriverLicenseDetailForm: FC<DriverGeneralDetailFormProps> = ({
  values,
  errors,
  touched,
  handleChange,
  formikSetValue,
  handleBlur,
  formikSetTouched,
  userCanEdit,
  loadingData,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.drivers.detailsPage.form",
  });
  return (
    <div className="px-5 pt-4 pb-8 bg-gray-100 grid grid-cols-12 gap-4">
      <AdminFormFieldDropdown
        loadingData={loadingData}
        label={t("license_type")}
        id="license_type"
        name="license_type"
        value={values.license_type}
        options={LICENSE_TYPE_OPTIONS}
        onChange={(e) => {
          formikSetValue("license_type", e?.value);
        }}
        onBlur={(e) => {
          formikSetTouched("license_type", true);
          handleBlur(e);
        }}
        error={errors.license_type}
        touched={touched.license_type}
        disabled={!userCanEdit}
        detailsFormField={true}
      />

      <AdminFormFieldDropdown
        loadingData={loadingData}
        label={t("license_state")}
        id="license_state"
        name="license_state"
        value={values.license_state}
        options={LICENSE_STATE_OPTIONS}
        onChange={(e) => {
          formikSetValue("license_state", e?.value);
        }}
        onBlur={(e) => {
          formikSetTouched("license_state", true);
          handleBlur(e);
        }}
        error={errors.license_state}
        touched={touched.license_state}
        disabled={!userCanEdit}
        detailsFormField={true}
      />

      <AdminFormFieldInput
        label={t("license_number")}
        type="text"
        id="license_number"
        name="license_number"
        value={values.license_number}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.license_number}
        touched={touched.license_number}
        disabled={!userCanEdit}
        detailsFormField={true}
      />

      <AdminFormFieldInput
        label={t("license_expiry_date")}
        type="date-picker"
        id="license_expiry_date"
        name="license_expiry_date"
        value={formattedDateTime(values.license_expiry_date, "yyyy-MM-dd")}
        onDatepickerChange={(_, value) => {
          formikSetValue("license_expiry_date", value);
        }}
        onBlur={(e) => {
          formikSetTouched("license_expiry_date", true);
          handleBlur(e);
        }}
        error={errors.license_expiry_date}
        touched={touched.license_expiry_date}
        disabled={!userCanEdit}
        detailsFormField={true}
        size="lg"
      />

      <AdminFormFieldDropdown
        loadingData={loadingData}
        label={t("license_status")}
        id="license_status"
        name="license_status"
        value={values.license_status}
        options={LICENSE_STATUS_OPTIONS}
        onChange={(e) => {
          formikSetValue("license_status", e?.value);
        }}
        onBlur={(e) => {
          formikSetTouched("license_status", true);
          handleBlur(e);
        }}
        error={errors.license_status}
        touched={touched.license_status}
        disabled={!userCanEdit}
        detailsFormField={true}
      />
    </div>
  );
};

export const DriverMedicalDetailForm: FC<DriverGeneralDetailFormProps> = ({
  values,
  errors,
  touched,
  handleChange,
  formikSetValue,
  handleBlur,
  formikSetTouched,
  userCanEdit,
  loadingData,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.drivers.detailsPage.form",
  });

  return (
    <div className="px-5 pt-4 pb-8 bg-gray-100 grid grid-cols-12 gap-4">
      <AdminFormFieldInput
        label={t("medical_card_no")}
        type="text"
        id="medical_card_no"
        name="medical_card_no"
        value={values.medical_card_no}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.medical_card_no}
        touched={touched.medical_card_no}
        disabled={!userCanEdit}
        detailsFormField={true}
      />

      <AdminFormFieldInput
        label={t("medical_card_expiry_date")}
        type="date-picker"
        id="medical_card_expiry_date"
        name="medical_card_expiry_date"
        value={formattedDateTime(values.medical_card_expiry_date, "yyyy-MM-dd")}
        onDatepickerChange={(_, value) => {
          formikSetValue("medical_card_expiry_date", value);
        }}
        onBlur={(e) => {
          formikSetTouched("medical_card_expiry_date", true);
          handleBlur(e);
        }}
        error={errors.medical_card_expiry_date}
        touched={touched.medical_card_expiry_date}
        disabled={!userCanEdit}
        detailsFormField={true}
        size="lg"
      />

      <div className="col-span-12 grid grid-cols-8">
        <AdminFormFieldCheckbox
          customWrapperClass="col-span-4 lg:mr-2"
          label={t("hazmat_certified")}
          id="hazmat_certified"
          type="checkbox"
          name="hazmat_certified"
          checked={values.hazmat_certified}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.hazmat_certified}
          touched={touched.hazmat_certified}
          disabled={!userCanEdit}
          detailsFormField={true}
        />
      </div>

      <AdminFormFieldInput
        label={t("twic")}
        type="text"
        id="twic"
        name="twic"
        value={values.twic}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.twic}
        touched={touched.twic}
        disabled={!userCanEdit}
        detailsFormField={true}
      />

      <AdminFormFieldInput
        label={t("twic_expiry_date")}
        type="date-picker"
        id="twic_expiry_date"
        name="twic_expiry_date"
        value={formattedDateTime(values.twic_expiry_date, "yyyy-MM-dd")}
        onDatepickerChange={(_, value) => {
          formikSetValue("twic_expiry_date", value);
        }}
        onBlur={(e) => {
          formikSetTouched("twic_expiry_date", true);
          handleBlur(e);
        }}
        error={errors.twic_expiry_date}
        touched={touched.twic_expiry_date}
        disabled={!userCanEdit}
        detailsFormField={true}
        size="lg"
      />

      <AdminFormFieldInput
        label={t("address")}
        type="text"
        id="address"
        name="address"
        value={values.address}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.address}
        touched={touched.address}
        disabled={!userCanEdit}
        detailsFormField={true}
        customWrapperClass="col-span-12"
      />
    </div>
  );
};
